import { useCallback, useEffect, useMemo } from 'react';
import { Box } from '@chakra-ui/react';

import {
  ContentSkip,
  Navbar,
  NavbarActionButtons,
  SpinnerModal,
  setUserAlias,
  removeUserAlias,
  getUserAlias,
} from 'shared-components';

import { UserRole } from '../../../store/auth/types';
import { hasPerm, P } from '../../../utilities/roles/permissions';
import { logout } from '../../../store/auth/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useSession } from 'shared-components/authentication';
import { ApplicationState } from '../../../store';
import { getAdmAllowedAliases } from '../../../store/config/actions';

interface IProps {
  isLoading: boolean;
  student_number: string;
  children: React.ReactNode;
  userRole: UserRole;
  groups: Array<string>;
  displayName?: string;
  contentWidth?: string;
  withLayout?: boolean;
}

function PageWrapper({
  isLoading,
  // student_number,
  userRole,
  groups,
  children,
}: IProps) {
  const dispatch = useDispatch();

  const canViewInsights = hasPerm(userRole, P.VIEW_INSIGHTS) || groups.includes('Insights');
  const canViewSettings = hasPerm(userRole, P.VIEW_CONFIG);
  const logOut = useCallback(() => dispatch(logout()), [dispatch]);

  const session = useSession();

  const onLogout = async () => {
    await session.onLogout();
    logOut();
  };

  // Get actAs data from Redux
  const { allowedAliases, currentUser, userName } = useSelector(({ auth, config }: ApplicationState) => {
    const isAdminOrEnroly = auth.role === UserRole.Admin || auth.role === UserRole.Enroly;
    return {
      allowedAliases: isAdminOrEnroly ? config.admAllowedAliases || [] : config.allowedAliases || [],
      currentUser: auth.currentUser,
      userName: auth.student_number,
    };
  });

  // Fetch ADM aliases for Admin/Enroly users
  const isAdminOrEnroly = userRole === UserRole.Admin || userRole === UserRole.Enroly;
  useEffect(() => {
    if (isAdminOrEnroly) {
      dispatch(getAdmAllowedAliases());
    }
  }, [isAdminOrEnroly, dispatch]);

  // Build Act As options
  const currentUserName = useMemo(() => {
    return currentUser?.givenName && currentUser?.familyName
      ? `${currentUser.givenName} ${currentUser.familyName}`
      : userName || '';
  }, [currentUser, userName]);

  const actAsOptions = useMemo(() => {
    return allowedAliases && allowedAliases.length > 0 ? [currentUserName, ...allowedAliases].filter(Boolean) : [];
  }, [allowedAliases, currentUserName]);

  const currentAlias = getUserAlias() || currentUserName;

  const handleActAsChange = useCallback(
    (selectedAlias: string) => {
      if (selectedAlias === currentUserName) {
        removeUserAlias();
      } else {
        setUserAlias(selectedAlias);
      }
      window.location.reload();
    },
    [currentUserName],
  );

  return (
    <>
      <SpinnerModal show={isLoading} />

      <ContentSkip />

      <Navbar
        actionableButtonsStates={{
          [NavbarActionButtons.STUDENTS]: {
            isVisible: true,
            isSelected: true,
          },
          [NavbarActionButtons.INSIGHTS]: {
            isVisible: canViewInsights,
          },
          [NavbarActionButtons.SETTINGS]: {
            isVisible: canViewSettings,
          },
          [NavbarActionButtons.FEEDBACK]: {
            isVisible: true,
          },
        }}
        username={currentUserName}
        isAuthenticated={true}
        onLogout={onLogout}
        actAs={
          actAsOptions.length > 1
            ? {
                options: actAsOptions,
                currentValue: currentAlias,
                onChange: handleActAsChange,
              }
            : undefined
        }
      />

      <Box ml={{ md: 'navbarWidth' }} pb={{ base: 'navbarHeight', md: 0 }}>
        {children}
      </Box>
    </>
  );
}

export default PageWrapper;
